import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery,
} from '@apollo/client';
import dayjs from 'dayjs';
import {
  API_STANDARD_DATE_TIME_TOP_OF_MINUTE,
  SHORT_DATE_FORMAT,
  SHORT_DATE_TIME_FORMAT,
  STANDARD_DATE_TIME_FORMAT,
} from '../../../config';
import { Maybe } from '../../../types';
import { getFrontlineUrl, isRhoOrCiSignal } from '../../../utils/environments';

import i18n from '../../../utils/i18n';

import {
  OutreachEvent,
  OutreachEventLocation,
  OutreachEventMapMarker,
} from '../components/MonitorInfo/types';
import { clampMonitorData } from '../helpers';

import { useEffect, useMemo, useState } from 'react';
import { MonitorData, OutreachStatCounts } from '../types';

import {
  getDailyMonitorDataQuery,
  getLocationsDataForDownload,
  getMapTriggeredOutreachEvents,
  getMonitorOutreachStats,
  getOutreachEventLocations,
  getTriggeredOutreachEvents,
} from './monitorQueries';

export const useGetDailyMonitorData = (
  monitorId: string,
  daysRange: number,
  skip?: boolean,
): {
  monitorDataLoading: boolean;
  monitorData: MonitorData[];
} => {
  const startTime = useMemo(
    () =>
      dayjs()
        .utc()
        .subtract(daysRange, 'days')
        .format(API_STANDARD_DATE_TIME_TOP_OF_MINUTE),
    [daysRange],
  );
  const endTime = useMemo(
    () => dayjs().utc().format(API_STANDARD_DATE_TIME_TOP_OF_MINUTE),
    [daysRange],
  );

  const query = useMemo(
    () => getDailyMonitorDataQuery(monitorId || '', startTime, endTime),
    [startTime, endTime, monitorId],
  );

  const [monitorData, setMonitorData] = useState<MonitorData[]>([]);

  const { loading, data } = useQuery(query, {
    fetchPolicy: 'network-only',
    skip: !monitorId || skip,
  });

  useEffect(() => {
    if (data?.rule_evaluation_result) {
      const res = data?.rule_evaluation_result
        .map((point: { ts: string; location_count: number }) => {
          return {
            ...point,
            ts: dayjs.utc(point.ts).local().format(SHORT_DATE_TIME_FORMAT),
          };
        })
        .reverse();

      const clampedData = clampMonitorData(res, daysRange);
      setMonitorData(clampedData);
    }
  }, [data]);

  return {
    monitorDataLoading: loading,
    monitorData: monitorData,
  };
};

export const useGetTriggeredOutreachEvents = (
  monitorId: string,
): {
  triggeredOutreachEventsDataLoading: boolean;
  triggeredOutreachEventsData: OutreachEvent[];
} => {
  const [outreachEvents, setOutreachEvents] = useState<OutreachEvent[]>([]);
  const { loading, data } = useQuery(
    getTriggeredOutreachEvents(monitorId || ''),
    {
      fetchPolicy: 'network-only',
      skip: !monitorId,
    },
  );

  useEffect(() => {
    if (data?.outreachEvents) {
      setOutreachEvents(data.outreachEvents);
    }
  }, [data]);

  return {
    triggeredOutreachEventsDataLoading: loading,
    triggeredOutreachEventsData: outreachEvents,
  };
};

export const useGetMapTriggeredOutreachEvents = (
  monitorId: string,
): {
  getMapOutreachEvents: LazyQueryExecFunction<any, OperationVariables>;
  triggeredMapOutreachEventsDataLoading: boolean;
  triggeredMapOutreachEventsData: OutreachEventMapMarker[];
} => {
  const [
    triggeredMapOutreachEventsData,
    setTriggeredMapOutreachEventsData,
  ] = useState<OutreachEventMapMarker[]>([]);
  const [getMapOutreachEvents, { loading, data }] = useLazyQuery(
    getMapTriggeredOutreachEvents(monitorId),
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (data?.outreachEvents) {
      const mappedData = data.outreachEvents.map((event: any) => ({
        locationId: event?.location_id,
        customerId: event?.customer_id,
        geoInfo: {
          lat: event?.latitude,
          long: event?.longitude,
        },
      }));
      setTriggeredMapOutreachEventsData(mappedData);
    }
  }, [data]);

  return {
    getMapOutreachEvents,
    triggeredMapOutreachEventsDataLoading: loading,
    triggeredMapOutreachEventsData,
  };
};

export const useGetOutreachStats = (
  monitorId: string,
  partnerId: string,
  daysRange: number,
): {
  outreachStatsDataLoading: boolean;
  outreachStatsData: OutreachStatCounts[];
} => {
  const daily = useMemo(() => daysRange !== 1 && daysRange !== 3, [daysRange]);
  const query = useMemo(
    () => getMonitorOutreachStats(monitorId || '', partnerId, daysRange),
    [monitorId, partnerId, daysRange],
  );
  const [outreachStatsData, setOutreachStatsData] = useState<
    OutreachStatCounts[]
  >([]);
  const { loading, data } = useQuery(query, {
    fetchPolicy: 'network-only',
    skip: !monitorId,
  });

  useEffect(() => {
    if (data?.stats) {
      const res = data?.stats?.map((entry: any) => ({
        ts: dayjs
          .utc(entry.ts)
          .local()
          .format(daily ? SHORT_DATE_FORMAT : SHORT_DATE_TIME_FORMAT),
        emailsSent: entry.support_clear_count + entry.support_start_count,
        webhookCount: entry.webhook_count + entry.webhook_error_count,
        pushUnreadCount:
          entry.subscriber_receive_count - entry.subscriber_open_count,
        pushReadCount: entry.subscriber_open_count,
        sendCount: entry.subscriber_send_count,
        receivedCount: entry.subscriber_receive_count,
        clickedCount: entry.subscriber_open_count,
        blockedCount: entry.subscriber_block_count,
        errorCount: entry.subscriber_error_count,
      }));
      setOutreachStatsData(res);
    }
  }, [data]);

  return {
    outreachStatsDataLoading: loading,
    outreachStatsData: outreachStatsData,
  };
};

export const useOutreachEventLocationsDownloadData = (
  partnerId: string,
  eventId: Maybe<string>,
  monitorId: string,
): {
  outreachEventLocationsDownloadDataLoading: boolean;
  outreachEventLocationsDownloadData: any;
} => {
  const { loading, error, data } = useQuery(
    getLocationsDataForDownload(eventId || '', monitorId, isRhoOrCiSignal()),
    {
      skip: partnerId === undefined || eventId === undefined,
      fetchPolicy: 'network-only',
    },
  );
  if (error) {
    console.error('Error loading monitor download data');
    return {
      outreachEventLocationsDownloadDataLoading: false,
      outreachEventLocationsDownloadData: undefined,
    };
  }

  const res = data?.locations.map(
    (location: {
      location_id: string;
      customer_id: string;
      account_id: string;
      created_at: string;
      cleared_at: string;
      sent_at: string;
    }) => {
      return {
        location_id: location.location_id,
        customer_id: location.customer_id,
        account_id: location.account_id,
        created_at: location.created_at,
        cleared_at: location.cleared_at,
        sent_at: location.sent_at,
      };
    },
  );
  return {
    outreachEventLocationsDownloadDataLoading: loading,
    outreachEventLocationsDownloadData: res,
  };
};

export const useGetOutreachEventLocations = (
  partnerId: string,
  eventId: Maybe<string>,
  page: number,
  monitorId: string,
): {
  outreachEventLocationsDataLoading: boolean;
  outreachEventLocationsData: OutreachEventLocation[] | undefined;
} => {
  const { loading, error, data } = useQuery(
    getOutreachEventLocations(eventId || '', 10, page, monitorId),
    {
      skip: partnerId === undefined || eventId === undefined,
      fetchPolicy: 'network-only',
    },
  );

  if (error) {
    console.error('Error loading outreach event locations data');
    return {
      outreachEventLocationsDataLoading: false,
      outreachEventLocationsData: undefined,
    };
  }

  const res = data?.locations?.map(
    (entry: {
      cleared_at: string;
      created_at: string;
      customer_id: string;
      location_id: string;
      sent_at: string;
    }) => {
      const durationDays = dayjs(entry.cleared_at).diff(
        dayjs(entry.created_at),
        'days',
      );
      const durationHours =
        dayjs(entry.cleared_at).diff(dayjs(entry.created_at), 'hours') % 24;
      const durationMinutes =
        dayjs(entry.cleared_at).diff(dayjs(entry.created_at), 'minutes') % 60;
      const duration = Boolean(
        durationDays || durationHours || durationMinutes,
      );
      return {
        locationId: entry.location_id,
        eventStatus: entry.cleared_at ? 'Resolved' : 'Unresolved',
        startTime: dayjs
          .utc(entry.created_at)
          .local()
          .format(STANDARD_DATE_TIME_FORMAT),
        endTime: entry.cleared_at
          ? dayjs
              .utc(entry.cleared_at)
              .local()
              .format(STANDARD_DATE_TIME_FORMAT)
          : '\u2014',
        duration: duration
          ? [
              durationDays && `${durationDays} ${i18n.t('genericLabels.days')}`,
              durationHours &&
                `${durationHours} ${i18n.t('genericLabels.hours')}`,
              durationMinutes &&
                `${durationMinutes} ${i18n.t('genericLabels.minutes')}`,
            ]
              .filter(Boolean)
              .join(' ')
          : '\u2014',
        sentTime: entry.sent_at
          ? dayjs
              .unix(Number(entry.sent_at) / 1000)
              .local()
              .format(STANDARD_DATE_TIME_FORMAT)
          : '\u2014',
        action: `${getFrontlineUrl()}/customer/${entry.customer_id}/location/${
          entry.location_id
        }/health`,
      };
    },
  );

  return {
    outreachEventLocationsDataLoading: loading,
    outreachEventLocationsData: res,
  };
};
